import { useCallback, useEffect, useState } from 'react';
import adminService, { type DroneStorageReport } from '../services/adminService';
import type { User } from '../types/auth';

function gb(bytes: number): string {
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function fileSize(bytes: number): string {
  if (bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function hoursField(seconds: number | undefined): string {
  if (seconds == null) return '10';
  const hours = seconds / 3600;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

function formatAnalysis(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.round((safe % 3600) / 60);
  if (hours && minutes) return `${hours} h ${minutes} min`;
  if (hours) return `${hours} h`;
  if (minutes) return `${minutes} min`;
  return '0 min';
}

export default function AdminDroneStorageModal({ user, onClose }: { user: User; onClose: () => void }) {
  const [report, setReport] = useState<DroneStorageReport | null>(null);
  const [runs, setRuns] = useState('5');
  const [storageGb, setStorageGb] = useState('10');
  const [analysisHours, setAnalysisHours] = useState('10');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    const data = await adminService.getDroneStorage(user.id);
    setReport(data);
    setRuns(String(data.runs_limit));
    setStorageGb((data.storage_limit / (1024 * 1024 * 1024)).toFixed(1));
    setAnalysisHours(hoursField(data.analysis_seconds_limit));
  }, [user.id]);

  useEffect(() => {
    void load().catch((err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load drone storage');
    });
  }, [load]);

  const saveCaps = async () => {
    setBusy(true);
    setError('');
    try {
      await adminService.setDroneQuota(user.id, {
        runs_per_month: Math.max(0, Number(runs) || 0),
        storage_limit_gb: Math.max(0, Number(storageGb) || 0),
        analysis_hours_per_month: Math.max(0, Number(analysisHours) || 0),
      });
      await load();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save the caps');
    } finally {
      setBusy(false);
    }
  };

  const removeFlight = async (jobId: string, name: string) => {
    if (!window.confirm(`Delete stored files for ${name}? This also removes that flight's monthly run.`)) return;
    setBusy(true);
    setError('');
    try {
      await adminService.deleteDroneFlight(user.id, jobId);
      await load();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not delete that flight');
    } finally {
      setBusy(false);
    }
  };

  const removeAll = async () => {
    if (!window.confirm(`Delete every finished flight stored for ${user.email}? A flight that is still running is left in place.`)) return;
    setBusy(true);
    setError('');
    try {
      await adminService.deleteDroneStorage(user.id);
      await load();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not delete this account’s flights');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="drone-storage-title">
      <div className="mt-8 w-full max-w-2xl rounded-xl bg-white p-4 text-gray-900 shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id="drone-storage-title" className="text-base font-semibold">
              Drone storage
            </h2>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900">
            Close
          </button>
        </div>
        {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
        {report && (
          <>
            <p className="mb-3 text-sm">
              {report.runs_used} of {report.runs_limit} runs this month · {gb(report.storage_used)} of {gb(report.storage_limit)} stored
              {report.analysis_seconds_limit != null
                ? ` · ${formatAnalysis(report.analysis_seconds_used ?? 0)} of ${formatAnalysis(report.analysis_seconds_limit)} analysis`
                : ''}
            </p>
            <div className="mb-4 flex flex-wrap items-end gap-2">
              <label className="text-xs text-gray-600">
                Runs / month
                <input
                  type="number"
                  min={0}
                  value={runs}
                  onChange={(event) => setRuns(event.target.value)}
                  className="mt-1 block w-24 rounded border px-2 py-1 text-sm"
                />
              </label>
              <label className="text-xs text-gray-600">
                Storage (GB)
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  value={storageGb}
                  onChange={(event) => setStorageGb(event.target.value)}
                  className="mt-1 block w-24 rounded border px-2 py-1 text-sm"
                />
              </label>
              <label className="text-xs text-gray-600">
                Analysis hours / month
                <input
                  type="number"
                  min={0}
                  step="0.5"
                  value={analysisHours}
                  onChange={(event) => setAnalysisHours(event.target.value)}
                  className="mt-1 block w-28 rounded border px-2 py-1 text-sm"
                />
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => void saveCaps()}
                className="rounded bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
              >
                Save caps
              </button>
              <button
                type="button"
                disabled={busy || report.flights.length === 0}
                onClick={() => void removeAll()}
                className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 disabled:opacity-50"
              >
                Delete all flights
              </button>
            </div>
            {report.flights.length === 0 ? (
              <p className="text-sm text-gray-500">No flights stored for this account.</p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {report.flights.map((flight) => (
                  <li key={flight.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">{flight.name}</p>
                        <p className="text-xs text-gray-500">
                          {flight.status} · {fileSize(flight.bytes)}
                          {flight.created_at ? ` · ${new Date(flight.created_at).toLocaleString()}` : ''}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={busy || flight.status === 'preparing' || flight.status === 'queued' || flight.status === 'running'}
                        onClick={() => void removeFlight(flight.id, flight.name)}
                        className="text-xs font-medium text-red-700 disabled:opacity-40"
                      >
                        Delete
                      </button>
                    </div>
                    <ul className="mt-2 space-y-0.5 text-xs text-gray-600">
                      {flight.files.map((file) => (
                        <li key={`${flight.id}-${file.label}`}>
                          {file.label}
                          {file.filename ? ` · ${file.filename}` : ''} · {file.linked ? 'linked, not counted' : fileSize(file.bytes)}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
